// Edición del pago de un gasto ya pagado (PagosEdificio → Detalle del Gasto).
//
// El pago vive en dos lugares: el registro de `payments` (fecha, método, monto
// y comprobante: lo que usa el estado de cuenta del fondo) y el gasto en
// `expenses` (paidDate y method: lo que usa el cierre para saber a qué período
// entra). Al corregir un dato se actualizan los dos juntos.

export const METODOS_PAGO_GASTO = ["Transferencia", "Tarjeta", "Efectivo"];

const fechaISO = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 10);
  if (typeof d === "number") return new Date(d).toISOString().slice(0, 10);
  if (d?.toDate) return d.toDate().toISOString().slice(0, 10);
  return "";
};

const fechaUY = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "—";
};

const redondear = (n) => Math.round(Number(n) * 100) / 100;

/** Formulario editable a partir del registro de pago. */
export function formPagoDesde(pago) {
  return {
    paymentDate: fechaISO(pago?.paymentDate),
    method: pago?.method || "",
    amount: pago?.amount != null && pago.amount !== "" ? String(pago.amount) : "",
    proof: null, // archivo nuevo (File); el actual queda en pago.proof
  };
}

/** Mensaje de error del formulario, o null si está bien. */
export function validarPagoEditado(form) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form?.paymentDate || "")) return "Ingresá la fecha de pago.";
  if (!form?.method) return "Elegí el método de pago.";
  const monto = Number(form?.amount);
  if (form?.amount === "" || !Number.isFinite(monto) || monto <= 0) return "El monto pagado debe ser un número positivo.";
  return null;
}

/**
 * Cambios entre el pago guardado y el formulario: [{ campo, etiqueta, antes, despues }]
 * con los valores ya listos para mostrar. Vacío si no cambió nada.
 */
export function cambiosDelPago(pago, form, formatoMonto = (n) => String(n)) {
  if (!pago || !form) return [];
  const cambios = [];
  const antes = formPagoDesde(pago);
  if (form.paymentDate !== antes.paymentDate) {
    cambios.push({ campo: "paymentDate", etiqueta: "Fecha de pago", antes: fechaUY(antes.paymentDate), despues: fechaUY(form.paymentDate) });
  }
  if (form.method !== antes.method) {
    cambios.push({ campo: "method", etiqueta: "Método de pago", antes: antes.method || "—", despues: form.method });
  }
  if (antes.amount === "" ? form.amount !== "" : redondear(form.amount) !== redondear(antes.amount)) {
    cambios.push({
      campo: "amount",
      etiqueta: "Monto pagado",
      antes: antes.amount === "" ? "—" : formatoMonto(Number(antes.amount)),
      despues: formatoMonto(redondear(form.amount)),
    });
  }
  if (form.proof) {
    cambios.push({
      campo: "proof",
      etiqueta: "Comprobante",
      antes: pago.proof ? "El actual (queda guardado en el historial)" : "Sin comprobante",
      despues: form.proof.name || "Archivo nuevo",
    });
  }
  return cambios;
}

/**
 * Updates para Firestore: { pago, gasto }. proofUrl es la URL del comprobante
 * nuevo ya subido (o null si no se cambió). El comprobante reemplazado queda en
 * comprobantesAnteriores para no perder el rastro.
 */
export function actualizacionesDelPago({ pago, form, proofUrl = null, actor, ahora }) {
  const datosPago = {
    paymentDate: form.paymentDate,
    method: form.method,
    amount: redondear(form.amount),
    updatedAt: ahora,
    updatedBy: actor,
  };
  if (proofUrl) {
    datosPago.proof = proofUrl;
    if (pago?.proof) datosPago.comprobantesAnteriores = [...(pago.comprobantesAnteriores || []), pago.proof];
  }
  return {
    pago: datosPago,
    gasto: { paidDate: form.paymentDate, method: form.method },
  };
}
